/**
 * StudioView — 3-pane layout orchestrator.
 *
 *   ┌─────────────┬──────────────────────────────────┬──────────────┐
 *   │ data panel  │      calendar (top bar +         │   agent      │
 *   │  (projects, │       week / day grid)           │   chat       │
 *   │   stats)    │                                  │              │
 *   └─────────────┴──────────────────────────────────┴──────────────┘
 *
 * Sidebars are resizable via ResizableDivider. Widths persist to
 * localStorage so the layout sticks across reloads.
 */

import { useCallback, useEffect, useState } from 'react'
import { ResizableDivider } from '@/features/develop/components/ResizableDivider'
import { CalendarWeekView } from './CalendarWeekView'
import { StudioChat } from './StudioChat'
import { StudioDataPanel } from './StudioDataPanel'
import { StudioTopBar } from './StudioTopBar'

const LEFT_WIDTH_KEY = 'studio:left-width'
const RIGHT_WIDTH_KEY = 'studio:right-width'

const DEFAULT_LEFT = 260
const DEFAULT_RIGHT = 360

const MIN_SIDEBAR = 220
const MAX_SIDEBAR = 600

function loadWidth(key: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback
  const raw = localStorage.getItem(key)
  if (!raw) return fallback
  const n = parseInt(raw, 10)
  if (Number.isNaN(n) || n < MIN_SIDEBAR || n > MAX_SIDEBAR) return fallback
  return n
}

export function StudioView() {
  const [leftWidth, setLeftWidth] = useState<number>(() => loadWidth(LEFT_WIDTH_KEY, DEFAULT_LEFT))
  const [rightWidth, setRightWidth] = useState<number>(() => loadWidth(RIGHT_WIDTH_KEY, DEFAULT_RIGHT))

  useEffect(() => {
    localStorage.setItem(LEFT_WIDTH_KEY, String(leftWidth))
  }, [leftWidth])
  useEffect(() => {
    localStorage.setItem(RIGHT_WIDTH_KEY, String(rightWidth))
  }, [rightWidth])

  const onResizeLeft = useCallback((delta: number) => {
    setLeftWidth((w) => Math.min(MAX_SIDEBAR, Math.max(MIN_SIDEBAR, w + delta)))
  }, [])
  const onResizeRight = useCallback((delta: number) => {
    // Right divider sits to the LEFT of the right pane, so dragging right
    // (positive delta) shrinks the right pane.
    setRightWidth((w) => Math.min(MAX_SIDEBAR, Math.max(MIN_SIDEBAR, w - delta)))
  }, [])

  return (
    <div className="h-screen w-screen flex flex-col bg-neutral-950 text-neutral-100">
      <StudioTopBar />

      <div className="flex-1 flex min-h-0">
        {/* LEFT — data / projects */}
        <aside
          className="flex-shrink-0 h-full border-r border-neutral-800 bg-neutral-900 overflow-hidden"
          style={{ width: leftWidth }}
        >
          <StudioDataPanel />
        </aside>
        <ResizableDivider onResize={onResizeLeft} />

        {/* CENTER — calendar */}
        <main className="flex-1 min-w-0 h-full overflow-hidden">
          <CalendarWeekView />
        </main>
        <ResizableDivider onResize={onResizeRight} />

        {/* RIGHT — agent chat */}
        <aside
          className="flex-shrink-0 h-full border-l border-neutral-800 bg-neutral-900 overflow-hidden"
          style={{ width: rightWidth }}
        >
          <StudioChat />
        </aside>
      </div>
    </div>
  )
}
